import { useEffect, useState, type FormEvent } from "react";
import { FiSearch } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import {
  getCompanyData,
  getCompanyFinancialData,
} from "../../../../../../shared/services/apis/tenantAnalytics";
import { useParams } from "react-router-dom";

import { getTenantById } from "../../../../../../shared/services/apis/tenant";

interface Company {
  name: string;
  profile_classification: string;
  profile_id: number;
}

interface FinancialData {
  id: string;
  title: string;
  currency: string;
  column: string;
  value: string;
  company_profile_id?: number;
}

const IncomeBenchmarking: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isUploadedData, ] = useState<boolean>(true);
  const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [financials, setFinancials] = useState<FinancialData[]>([]);

 const {tenantId}=useParams();

  // Utility function to format financial values in millions/billions
  const formatFinancialValue = (value: string | number): string => {
    if (!value || value === "-" || value === "") return "-";

    // Remove any non-numeric characters except decimal points and negative signs
    const cleanValue = String(value).replace(/[^\d.-]/g, "");
    const numValue = parseFloat(cleanValue);

    if (isNaN(numValue)) return String(value);

    const absValue = Math.abs(numValue);
    const isNegative = numValue < 0;
    const sign = isNegative ? "-" : "";

    if (absValue >= 1000000000) {
      // Billions
      const billions = (absValue / 1000000000).toFixed(1);
      return `${sign}${billions}B`;
    } else if (absValue >= 1000000) {
      // Millions
      const millions = (absValue / 1000000).toFixed(1);
      return `${sign}${millions}M`;
    } else if (absValue >= 1000) {
      // Thousands
      const thousands = (absValue / 1000).toFixed(1);
      return `${sign}${thousands}K`;
    } else {
      // Less than 1000, show as is but formatted
      return `${sign}${absValue.toLocaleString()}`;
    }
  };

  // Function to extract and set available years from financial data
  const updateAvailableYears = (financialData: FinancialData[]) => {
    const years = [...new Set(financialData.map((item) => item.column))]
      .filter((year) => year && !isNaN(Number(year)))
      .sort((a, b) => Number(b) - Number(a));

    setAvailableYears(years);

    // Set the most recent year as default if no year is selected
    if (years.length > 0 && !selectedYear) {
      setSelectedYear(years[0]);
    }
  };

  const fetchCompanyData = async (name: string) => {
    setError(null);

    try {
      const response = await getCompanyData(name);

      console.log("Company search response:", response.data);

      const companyData = response.data || response;

      if (!companyData || companyData.length === 0) {
        setError("No companies found for this search term");
        return;
      }

      // Normalize to array
      const companiesArray: Company[] = Array.isArray(companyData)
        ? companyData
        : [companyData];

      // Try to pick exact-name matches first (if any)
      const exactMatches = companiesArray.filter((c) => c.name === name);

      // If we have exact matches use them, otherwise fall back to the returned array
      const finalMatches =
        exactMatches.length > 0 ? exactMatches : companiesArray;

      // Filter out companies that are already present (by profile_id) to prevent duplicates
      const existingIds = new Set(companies.map((c) => c.profile_id));
      const newCompanies = finalMatches.filter(
        (c) => !existingIds.has(c.profile_id)
      );

      if (newCompanies.length === 0) {
        setError("Company already added or no new matches found.");
        return;
      }

      // Append new companies to state (keeps previous ones)
      setCompanies((prev) => {
        const merged = [...prev, ...newCompanies];
        return merged;
      });

      // Fetch financials for newly appended companies
      newCompanies.forEach((c) => fetchFinancialData(c.profile_id));
    } catch (err: any) {
      console.error("Error fetching company data:", err);
      setError( "Financial data not available for this company");
    }
  };

  const fetchFinancialData = async (profileId: number) => {
    setError(null);

    try {
      console.log("Fetching financial data for profile ID:", profileId);

      const response = await getCompanyFinancialData(profileId);
      console.log("Financial data response:", response);

      // Handle response data
      const responseData = response.data || response;

      if (!Array.isArray(responseData)) {
        console.warn("Financial data is not an array:", responseData);
        return;
      }

      // Map the financial data with proper company profile ID
      const mappedData: FinancialData[] = responseData.map((item: any) => ({
        id: String(item.company_profile_id || item.profile_id || item.id),
        title: item.title,
        currency: item.currency,
        column: item.column,
        value: item.value,
        company_profile_id:
          item.company_profile_id ?? item.profile_id ?? undefined,
      }));

      console.log("Mapped financial data:", mappedData);

      // Append mappedData but prevent duplicates (company_profile_id + title + column)
      setFinancials((prev) => {
        const toAdd = mappedData.filter((newItem) => {
          const newCompanyId = Number(newItem.company_profile_id || newItem.id);
          return !prev.some((p) => {
            const prevCompanyId = Number(p.company_profile_id || p.id);
            return (
              prevCompanyId === newCompanyId &&
              p.title === newItem.title &&
              p.column === newItem.column
            );
          });
        });

        const newFinancials = [...prev, ...toAdd];
        updateAvailableYears(newFinancials);
        return newFinancials;
      });
    } catch (err: any) {
      console.error("Error fetching financial data:", err);
      setError("Financial data not available for this company");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      fetchCompanyData(searchTerm.trim());
    }
  };

  const handleDeleteColumn = (index: number) => {
    console.log(`Delete column at index: ${index}`);
    const companyToRemove = companies[index];
    if (companyToRemove) {
      setCompanies((prev) => prev.filter((_, i) => i !== index));
      const newFinancials = financials.filter(
        (item) =>
          Number(item.company_profile_id || item.id) !==
          companyToRemove.profile_id
      );
      setFinancials(newFinancials);
      updateAvailableYears(newFinancials);
    }
  };





  useEffect(() => {
    const fetchTenantAndCompany = async () => {
      if (!tenantId) return;

      try {
        // 1. Get tenant details
        const tenantResponse = await getTenantById(tenantId);
        console.log("Tenant response:", tenantResponse);

        // Adjust based on your API response shape
        const tenantData = tenantResponse || tenantResponse;
        const tenantName = tenantData?.name;

        if (tenantName) {
          // 2. Call existing fetchCompanyData with tenant name
          await fetchCompanyData(tenantName);
        } else {
          console.warn("Tenant name not found in API response");
        }
      } catch (err) {
        console.error("Error fetching tenant:", err);
      }
    };

    fetchTenantAndCompany();
  }, [tenantId]); // 👈 reruns if tenantId changes


  //  function to organize financial data by metric and company (filtered by selected year)
  const organizeFinancialData = () => {
    // Filter financials by selected year if a year is selected
    const filteredFinancials = selectedYear
      ? financials.filter((item) => item.column === selectedYear)
      : financials;

    if (filteredFinancials.length === 0) {
      return { allMetrics: [], financialsByCompany: {} };
    }

    // Get all unique metrics/titles from filtered data
    const allMetrics = [
      ...new Set(filteredFinancials.map((item) => item.title)),
    ];

    // Group financial data by company profile ID (from filtered data)
    const financialsByCompany = filteredFinancials.reduce((acc, item) => {
      // Use company_profile_id first, then fallback to id
      const companyId = item.company_profile_id || Number(item.id);
      if (!acc[companyId]) {
        acc[companyId] = {};
      }
      acc[companyId][item.title] = item;
      return acc;
    }, {} as Record<number, Record<string, FinancialData>>);

    console.log("Organized data (filtered):", {
      allMetrics,
      financialsByCompany,
      selectedYear,
    });
    return { allMetrics, financialsByCompany };
  };
  const organizeFinancialDataByYear = () => {
    if (financials.length === 0) {
      return { allMetrics: [], financialsByYear: {} };
    }

    // Get all unique metrics/titles
    const allMetrics = [...new Set(financials.map((item) => item.title))];

    // Group financial data by year
    const financialsByYear = financials.reduce((acc, item) => {
      if (!acc[item.column]) {
        acc[item.column] = {};
      }
      if (!acc[item.column][item.title]) {
        acc[item.column][item.title] = [];
      }
      acc[item.column][item.title].push(item);
      return acc;
    }, {} as Record<string, Record<string, FinancialData[]>>);

    return { allMetrics, financialsByYear };
  };

  const { allMetrics, financialsByCompany } = organizeFinancialData();

  return (
    <div className="max-w-4xl m-auto p-6 bg-[#FDFDFD] rounded-2xl  mt-0 2xl:mt-20  mb-30 shadow-sm">
      {/* Title */}
      <h2 className="text-2xl font-light text-gray-800 mb-1">
        Income Benchmarking
      </h2>
      <p className="text-sm text-gray-700 mb-2 leading-snug capitalize">
        Financial data is provided by publicly available data, Analyst estimates may not be reflective of actual data
      </p>
      {/* <span>
        <button
          onClick={() => setShowUploadDialog(true)}
          className="text-blue-700 text-sm hover:underline inline-block mb-4 cursor-pointer"
        >
          Upload financial data
        </button>
        <span className="text-gray-700 text-sm">
          {" "}
          for more accurate analysis
        </span>
      </span> */}

      {/* Year Tabs */}
      <div className="flex items-center gap-2 mt-2 mb-4">
        <div className="max-w-[16vw] 2xl:max-w-[12vw] overflow-x-auto flex space-x-2" >
        {availableYears.map((year) => (
          
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-150 cursor-pointer ${
                year === selectedYear
                  ? "bg-gray-100 text-blue-700 border border-gray-200"
                  : "text-gray-800 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              {year}
            </button>
        ))}
        </div>

        {/* Add Company Search */}
        <form
          onSubmit={handleSearchSubmit}
          className="ml-auto flex items-center border border-gray-300 rounded-lg px-3 py-1.5"
        >
          <input
            type="text"
            placeholder="Add a company"
            value={searchTerm}
            onChange={handleSearchChange}
            className="outline-none text-sm bg-transparent placeholder-gray-700 w-70"
          />
          <button type="submit">
            <FiSearch className="text-gray-400 mr-2 cursor-pointer" />
          </button>
        </form>
      </div>

      {/* Error Handling */}
      {error && (
        <p className=" text-gray-600 text-sm mb-4 p-2 bg-gray-100 rounded">
          {error}
        </p>
      )}

      {/* Debug Info */}
      <div className="text-xs text-gray-500 mb-4">
        Companies: {companies.length} | Financial Records: {financials.length} |
        Filtered Records:{" "}
        {selectedYear
          ? financials.filter((f) => f.column === selectedYear).length
          : financials.length}{" "}
        | Unique Metrics: {allMetrics.length} | Selected Year:{" "}
        {selectedYear || "All"}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <div className="border border-gray-200 rounded-2xl overflow-hidden">
          {companies.length > 0 ? (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-3 w-50 text-left border-r border-gray-200 text-gray-500 font-medium text-xs">
                    Financial Metric
                    <br />
                    <span className="text-xs font-normal">
                      {selectedYear ? `Year: ${selectedYear}` : "All Years"}
                    </span>
                  </th>
                  {companies.map((c, idx) => (
                    <th
                      key={idx}
                      className={`py-3 text-center border-r min-w-32 last:border-r-0 border-gray-200 font-medium text-xs cursor-pointer hover:bg-gray-100 ${
                        hoveredColumn === idx ? "bg-gray-100" : ""
                      }`}
                      onMouseEnter={() => setHoveredColumn(idx)}
                      onMouseLeave={() => setHoveredColumn(null)}
                    >
                      {c.name}
                      <br />
                      <span className="text-xs font-normal text-gray-500">
                        {hoveredColumn === idx ? (
                          <button
                            onClick={() => handleDeleteColumn(idx)}
                            className="text-red-500 flex items-center text-xs m-auto hover:underline cursor-pointer"
                          >
                            <RiDeleteBinLine className="text-red-500 h-3 w-3 mr-1" />
                            Delete
                          </button>
                        ) : (
                          <span
                            className={`${
                              idx === 0 ? "text-blue-600 underline" : ""
                            }`}
                          >
                            {c.profile_classification}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allMetrics.length > 0 ? (
                  allMetrics.map((metric, i) => (
                    <tr
                      key={i}
                      className="border-b last:border-b-0 border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      {/* Metric / Title */}
                      <td className="py-3 px-3 text-gray-700 border-r text-light border-gray-200 align-top">
                        {metric}
                        {(() => {
                          const filteredFinancials = selectedYear
                            ? financials.filter(
                                (f) => f.column === selectedYear
                              )
                            : financials;
                          const firstDataPoint = filteredFinancials.find(
                            (f) => f.title === metric
                          );
                          return firstDataPoint?.currency ? (
                            <span className="block text-xs text-gray-400 mt-0.5">
                              {firstDataPoint.currency}
                            </span>
                          ) : null;
                        })()}
                      </td>

                      {/* Values for each company */}
                      {companies.map((company, companyIdx) => {
                        const companyFinancialData =
                          financialsByCompany[company.profile_id];
                        const metricData = companyFinancialData?.[metric];

                        return (
                          <td
                            key={companyIdx}
                            className="py-5 text-gray-700 text-center align-top border-r last:border-r-0 border-gray-200"
                          >
                            {metricData ? (
                              <>
                                {formatFinancialValue(metricData.value) || "-"}
                                {!selectedYear && metricData.column && (
                                  <span className="block text-xs text-gray-400 mt-0.5">
                                    {metricData.column}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={companies.length + 1}
                      className="py-8 text-center text-gray-500"
                    >
                      {financials.length > 0
                        ? `No financial data available for ${
                            selectedYear
                              ? `year ${selectedYear}`
                              : "selected criteria"
                          }`
                        : "No financial data available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-gray-500">
              Search for a company to view financial data
            </div>
          )}
        </div>
      </div>

      {/* Upload Dialog - keeping the same as before */}
      {showUploadDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/20 z-50">
          <div
            className="bg-white rounded-[20px] shadow-lg relative border border-gray-200"
            style={{ width: "738px", height: "727px" }}
          >
            <button
              onClick={() => setShowUploadDialog(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 text-xl cursor-pointer"
            >
              ✕
            </button>

            <div className="p-6 h-full flex flex-col">
              <h2 className="text-[26px] font-light text-gray-800 mb-1">
                Income Statement
              </h2>
              <p className="text-sm text-gray-700 mb-2 leading-snug capitalize">
                Financial data is provided by publicly available data, Analyst estimates may not be reflective of actual data
              </p>

              {/* <p className="mb-2">
                <span className="text-blue-700 text-sm">
                  Upload financial data
                </span>
                <span className="text-gray-700 text-sm">
                  {" "}
                  for a more accurate analysis
                </span>
              </p> */}

              <div className="flex flex-row py-8 gap-4">
                <button
                  onClick={() => setIsActive(!isActive)}
                  className={`px-4 py-2 group rounded-full text-sm font-light transition-colors duration-150 cursor-pointer ${
                    isActive
                      ? "bg-gray-100 text-blue-700 border border-gray-200"
                      : "text-gray-800 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  Analyst Estimate
                </button>
                <button
                  onClick={() => setIsActive(false)}
                  className={`px-4 py-2 group rounded-full text-sm font-light transition-colors duration-150 cursor-pointer ${
                    isUploadedData
                      ? "bg-gray-100 text-blue-700 border border-gray-200"
                      : "text-gray-800 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  Uploaded Data
                </button>
              </div>

              {isActive ? (
                // Show table when Analyst Estimate is active
                <div className="border border-gray-200 rounded-xl flex-1 overflow-hidden">
                  <div className="overflow-auto h-full">
                    {financials.length > 0 ? (
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 ">
                            <th className="px-3 py-3 text-left border-r border-gray-200 text-gray-500 font-medium text-xs">
                              Financial Metric
                            </th>
                            {availableYears.map((year) => (
                              <th
                                key={year}
                                className="py-3 px-3 text-center border-r text-gray-400 last:border-r-0 border-gray-200 font-medium text-xs"
                              >
                                {year}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const { allMetrics, financialsByYear } =
                              organizeFinancialDataByYear();
                            return allMetrics.map((metric, i) => (
                              <tr
                                key={i}
                                className="border-b last:border-b-0 border-gray-100 hover:bg-gray-50 transition-colors"
                              >
                                <td className="py-3 px-3 text-gray-700 border-r border-gray-200 align-top font-medium">
                                  {metric}
                                  {(() => {
                                    const firstDataPoint = financials.find(
                                      (f) => f.title === metric
                                    );
                                    return firstDataPoint?.currency ? (
                                      <span className="block text-xs text-gray-400 mt-0.5 font-normal">
                                        {firstDataPoint.currency}
                                      </span>
                                    ) : null;
                                  })()}
                                </td>
                                {availableYears.map((year) => {
                                  const yearData =
                                    financialsByYear[year]?.[metric];
                                  const value = yearData?.[0]?.value; // Take first value if multiple

                                  return (
                                    <td
                                      key={year}
                                      className="py-3 px-3 text-gray-700 text-center align-top "
                                    >
                                      {formatFinancialValue(value) || "-"}
                                    </td>
                                  );
                                })}
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        No financial data available for analyst estimates
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Show file upload area when not active
                <div className="border border-gray-200 rounded-xl p-12 text-center transition flex-1 flex flex-col justify-center">
                  <p className="text-gray-800 text-left font-light mx-10 mb-2">
                    Upload Financial Data for the AI to process
                  </p>
                  <label
                    htmlFor="file-upload"
                    className="block cursor-pointer border border-dashed mx-10 border-gray-300 rounded-xl py-10"
                  >
                    <span className="text-gray-800">
                      Drop your files here or{" "}
                      <span className="text-blue-600 hover:underline">
                        browse
                      </span>
                      <input id="file-upload" type="file" className="hidden" />
                      <p className="text-xs text-gray-400 mt-1">
                        .csv, .xlsx, .pdf, .docx etc. Up to 50 MB
                      </p>
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeBenchmarking;
